import { isExternalHttpsRedirect } from '@common/core-layer/utils/external-redirect.utils'

import {
  formatEventDateLabel,
  formatEventTimeLabel,
  formatEventWeekdayTimeLabel,
  isEventLiveNow,
  isOnlineEventLocation,
  resolveEventDisplayTimeZone,
  toEventLocationLabel,
  toEventLocationParts,
} from './map-events-management-event'
import type { EventDetailViewModel, EventDetailVisibleSections } from '../types/event-detail'

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : undefined

const asString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')

const ENTITY_MAP: Record<string, string> = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
}

export const stripEventHtml = (value: string): string =>
  value
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;|&amp;|&lt;|&gt;|&quot;|&#39;/gi, match => ENTITY_MAP[match.toLowerCase()] ?? ' ')
    .replace(/\s+/g, ' ')
    .trim()

export const EVENT_DETAIL_VISIBLE_SECTIONS_ALL: EventDetailVisibleSections = {
  about: true,
  agenda: true,
  speakers: true,
  attendees: true,
  exhibitors: true,
  tickets: true,
}

const EVENT_DETAIL_VISIBLE_SECTIONS_FROM_API: EventDetailVisibleSections = {
  about: false,
  agenda: false,
  speakers: false,
  attendees: false,
  exhibitors: true,
  tickets: false,
}

const PAGE_SECTION_TO_FLAG = {
  about: 'about',
  schedule: 'agenda',
  speakers: 'speakers',
  tickets: 'tickets',
  attendees: 'attendees',
} as const

export const mapEventPageSections = (value: unknown): EventDetailVisibleSections => {
  if (!Array.isArray(value) || value.length === 0) {
    return { ...EVENT_DETAIL_VISIBLE_SECTIONS_ALL }
  }

  const visible = { ...EVENT_DETAIL_VISIBLE_SECTIONS_FROM_API }

  for (const entry of value) {
    const section = asRecord(entry)
    if (!section) continue
    const key = asString(section.key)
    const flag = PAGE_SECTION_TO_FLAG[key as keyof typeof PAGE_SECTION_TO_FLAG]
    if (!flag) continue
    visible[flag] = section.hidden !== true
  }

  return { ...visible, exhibitors: true }
}

const isLikelyEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

const toFormatLabel = (locationType: string): string => {
  const type = locationType.toLowerCase()
  if (type === 'online' || type === 'virtual') return 'Online'
  if (type === 'in-person' || type === 'in_person') return 'In person'
  return locationType ? locationType : 'In person'
}

export interface MappedEventDetail {
  readonly item: EventDetailViewModel
  readonly displayFormat: '12h' | '24h'
  readonly timezone?: string
  readonly isOnline: boolean
}

export const mapEventDetail = (value: unknown, now: Date = new Date()): MappedEventDetail | undefined => {
  const event = asRecord(value)
  if (!event) return undefined

  const id = asString(event.id)
  const title = asString(event.name)
  if (!id || !title) return undefined

  const startDateTime = asString(event.startDateTime)
  const endDateTime = asString(event.endDateTime)
  const timezone = asString(event.timezone) || undefined
  const location = asRecord(event.location)
  const organizer = asRecord(event.organizer)
  const displayTimeZone = resolveEventDisplayTimeZone(location, timezone)
  const displayFormat = asString(event.displayFormat) === '24h' ? '24h' : '12h'
  const dateLabel = formatEventDateLabel(startDateTime, endDateTime, displayTimeZone)
  const timeLabel =
    formatEventWeekdayTimeLabel(startDateTime, displayTimeZone, displayFormat) ||
    formatEventTimeLabel(startDateTime, displayTimeZone, displayFormat)
  const locationLabel = toEventLocationLabel(location)
  const locationParts = toEventLocationParts(location)
  const locationType = asString(location?.type)
  const about = stripEventHtml(asString(event.description) || asString(event.summary))
  const organizerImageUrl = asString(organizer?.imageUrl)
  const organizerEmail = asString(organizer?.email)
  const organizerPhone = asString(organizer?.phone)
  const organizerWebsite = asString(organizer?.website)
  const eventUrl = asString(event.eventUrl)

  return {
    displayFormat,
    timezone,
    isOnline: isOnlineEventLocation(location),
    item: {
      id,
      title,
      imageUrl: asString(event.coverImage),
      dateLabel,
      locationLabel,
      ...(timeLabel ? { timeLabel } : {}),
      ...(locationParts.venue ? { locationVenue: locationParts.venue } : {}),
      ...(locationParts.address ? { locationAddress: locationParts.address } : {}),
      ...(isExternalHttpsRedirect(eventUrl) ? { eventUrl } : {}),
      about,
      organizerName: asString(organizer?.name),
      ...(isLikelyEmail(organizerEmail) ? { organizerEmail } : {}),
      ...(organizerPhone ? { organizerPhone } : {}),
      ...(isExternalHttpsRedirect(organizerWebsite) ? { organizerWebsite } : {}),
      ...(organizerImageUrl ? { organizerImageUrl } : {}),
      formatKind: isOnlineEventLocation(location) ? 'online' : 'in-person',
      agendaSummary: '',
      agendaDays: [],
      speakers: [],
      attendees: [],
      exhibitors: [],
      tickets: [],
      visibleSections: mapEventPageSections(event.pageSections),
      details: [
        ...(dateLabel ? [{ key: 'Dates', value: dateLabel }] : []),
        ...(timeLabel ? [{ key: 'Time', value: timeLabel }] : []),
        ...(locationLabel ? [{ key: 'Location', value: locationLabel }] : []),
        { key: 'Format', value: toFormatLabel(locationType) },
      ],
      ...(isEventLiveNow(startDateTime, endDateTime, now) ? { isLive: true } : {}),
    },
  }
}
